import { useState } from 'react'

type FormDataUpdater<T> = T | ((prevValue: T) => T) | string | number | boolean | File | null | undefined

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFormData = <T extends Record<string, any>>(initialFormData: T) => {
  const [formData, setFormData] = useState<T>(initialFormData)

  const handleDataChange = <K extends keyof T>(key: K, newValue: FormDataUpdater<T[K]>) => {
    // if a function is passed, invoke it
    // with the previous val as an argument
    if (typeof newValue === 'function') {
      setFormData((currentData) => {
        const currKeyVal = currentData[key]
        const newData = (newValue as (prevValue: T[K]) => T[K])(currKeyVal)
        return {
          ...currentData,
          [key]: newData,
        }
      })
      return
    }

    // otherwise just set the new value
    setFormData((currentData) => ({
      ...currentData,
      [key]: newValue as T[K],
    }))
  }

  const checkInputIsValid = <K extends keyof T>(key: K, condition: boolean, fallbackVal?: T[K]) => {
    if (!condition) {
      setFormData((currentData) => ({
        ...currentData,
        [key]: fallbackVal ?? ('' as T[K]),
      }))
    }
  }

  return {
    formData,
    setFormData,
    handleDataChange,
    checkInputIsValid,
  }
}
